import React from 'react';
import {
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Checkbox,
  Skeleton,
  TableFooter,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useGetSetOffDetailsQuery, useCreateSetOffDetailsMutation } from '../api/setoff.api';
import { MoneyFormatv3 } from '../../../library/MoneyFormat';
import { formatUnixTimestamp } from '../../../library/dayJs';
import { MoneyTextField } from '../../../components';
import { showWarningSnackbar, showSuccessSnackbar } from '../../../components/snackBar/snakbar';
import { Modal } from '../../../components/modals/Modal';
import { CustomTableCell } from '../../../pages/LedgerTransaction/styles';

export interface SetOffModalProps {
  open: boolean;
  onClose: () => void;
  voucherType: string;
  voucherId: number;
  voucherNumber: string;
  accountid: number | null;
  onSetOffSuccess?: () => void;
}

export const SetOffModal: React.FC<SetOffModalProps> = ({ open, onClose, voucherType, voucherId, voucherNumber, accountid, onSetOffSuccess }) => {
  const [applyToAmounts, setApplyToAmounts] = useState<Record<string, number>>({});
  const [checkedRows, setCheckedRows] = useState<Record<string, boolean>>({});
  const [initialLinkedToTarget, setInitialLinkedToTarget] = useState<Record<string, number>>({});
  const [EnteredSetOff, setEnteredSetOff] = useState<number>(0);
  const [balancetoSetoff, setBalancetoSetoff] = useState<number>(0);
  const [totalInvoiceAmount, setTotalInvoiceAmount] = useState<number>(0);
  const dataVersionRef = useRef<number>(0);

  const {
    data: setOffData = [],
    isLoading: loading,
    error,
    refetch,
  } = useGetSetOffDetailsQuery(
    {
      invoiceid: voucherId,
      transactiontype: voucherType,
      accountid: accountid,
    },
    {
      skip: !voucherId || !voucherType || !open,
      refetchOnMountOrArgChange: true,
    }
  );

  useEffect(() => {
    if (open) {
      setApplyToAmounts({});
      setCheckedRows({});
      setInitialLinkedToTarget({});
      setEnteredSetOff(0);
      setBalancetoSetoff(0);
      setTotalInvoiceAmount(0);
    }
  }, [open, voucherId]);

  // Initialize amounts from linked_to_target when data loads
  useEffect(() => {
    if (!open) return;
    
    if (setOffData && setOffData.length > 0) {
      const filteredData = setOffData.filter((item) => voucherId !== item?.invoiceid) || [];
      const initialAmounts: Record<string, number> = {};
      const initialChecked: Record<string, boolean> = {};
      const initialLinked: Record<string, number> = {};
      let totalLinkedToTarget = 0;
      let totalAvailableAmount = 0;

      filteredData.forEach((item) => {
        const invoiceId = item?.invoiceid?.toString() || '';
        const linkedToTarget = item?.linked_to_target || 0;
        const availableAmount = item?.available_amount || 0;

        // Sum up all linkedToTarget values for EnteredSetOff
        totalLinkedToTarget += linkedToTarget;
        // Sum up all available_amount values for balancetoSetoff
        totalAvailableAmount += availableAmount;

        // Track initial linked_to_target values (only if > 0)
        if (linkedToTarget > 0) {
          initialLinked[invoiceId] = linkedToTarget;
          initialAmounts[invoiceId] = linkedToTarget;
          initialChecked[invoiceId] = true;
        }
      });

      // Calculate sum of invoiceamount_converted from setOffData
      const sum = filteredData.reduce((total, item) => {
        return total + (item?.invoiceamount_converted || 0);
      }, 0);

      setInitialLinkedToTarget(initialLinked);
      setEnteredSetOff(totalLinkedToTarget);
      setBalancetoSetoff(totalAvailableAmount);
      setTotalInvoiceAmount(sum);

      setApplyToAmounts(initialAmounts);
      setCheckedRows(initialChecked);
    } else {
      setTotalInvoiceAmount(0);
      setApplyToAmounts({});
      setCheckedRows({});
      setInitialLinkedToTarget({});
      setEnteredSetOff(0);
      setBalancetoSetoff(0);
    }
  }, [setOffData, voucherId, open]);

  const [createSetOffDetails, { isLoading: isCreating }] = useCreateSetOffDetailsMutation();

  // Find the current voucher's setoff balance from the data
  const currentVoucher = setOffData?.find((item) => voucherId === item?.invoiceid);
  const balance = currentVoucher?.setoff_balance ?? 0;
  const invoiceAmount = currentVoucher?.invoiceamount_converted ?? 0;

  console.log({ voucherId, voucherType, voucherNumber, setOffData, error, balance, currentVoucher });

  const handleRetry = () => {
    refetch();
  };

  const handleAmountChange = (invoiceId: string, amount: number | null) => {
    const newAmount = amount || 0;

    // Find the row's available_amount - filter out current voucher first
    const filteredData = setOffData?.filter((item) => voucherId !== item?.invoiceid) || [];
    const row = filteredData.find((item) => item?.invoiceid?.toString() === invoiceId);
    const rowAvailableAmount = row?.available_amount || 0;
    const initialLinkedAmount = initialLinkedToTarget[invoiceId] || 0;

    // Maximum allowed for this row: available_amount + initial linked_to_target
    // (because the existing linked_to_target is being released and can be reused)
    const rowMaxAmount = rowAvailableAmount + initialLinkedAmount;

    // Validate against row's max amount (available_amount + initial linked_to_target)
    if (newAmount > rowMaxAmount) {
      showWarningSnackbar(
        `Amount cannot exceed this voucher's maximum amount (₹${MoneyFormatv3(rowMaxAmount)}) ` +
          `(Available: ₹${MoneyFormatv3(rowAvailableAmount)} + Existing: ₹${MoneyFormatv3(initialLinkedAmount)})`
      );
      return;
    }

    // Calculate total amount after this change
    // Need to account for the fact that initialLinkedToTarget was already counted in the total
    const currentTotal = Object.values(applyToAmounts).reduce((sum, val) => sum + val, 0);
    const currentRowAmount = applyToAmounts[invoiceId] || 0;

    // When calculating new total, we need to:
    // 1. Remove current row amount from total
    // 2. Add new amount
    // But we also need to account for initialLinkedToTarget that was already allocated
    // The effective change is: (newAmount - currentRowAmount)
    const newTotal = currentTotal - currentRowAmount + newAmount;

    // Validate that total doesn't exceed invoiceAmount
    if (newTotal > invoiceAmount) {
      const remainingBalance = invoiceAmount - (currentTotal - currentRowAmount);
      const maxAllowed = Math.min(remainingBalance, rowMaxAmount);
      showWarningSnackbar(
        `Total amount cannot exceed invoice amount (₹${MoneyFormatv3(invoiceAmount)}). ` +
          `Maximum allowed for this row: ₹${MoneyFormatv3(maxAllowed)}`
      );
      return;
    }

    setApplyToAmounts((prev) => ({
      ...prev,
      [invoiceId]: newAmount,
    }));

    // Auto-check checkbox if amount is entered
    if (newAmount > 0) {
      setCheckedRows((prev) => ({
        ...prev,
        [invoiceId]: true,
      }));
    } else {
      setCheckedRows((prev) => ({
        ...prev,
        [invoiceId]: false,
      }));
    }
  };

  const handleCheckboxChange = (invoiceId: string, checked: boolean, rowAvailableAmount: number, linkedToTarget?: number) => {
    if (checked) {
      // Row max amount: available_amount + initial linked_to_target
      // (because the existing linked_to_target is being released and can be reused)
      const initialLinkedAmount = initialLinkedToTarget[invoiceId] || 0;
      const rowMaxAmount = rowAvailableAmount + initialLinkedAmount;

      // Calculate remaining balance available (invoiceAmount minus what's already allocated to other rows)
      const currentRowAmount = applyToAmounts[invoiceId] || 0;
      const totalAllocatedToOthers = Object.values(applyToAmounts).reduce((sum, val) => sum + val, 0) - currentRowAmount;
      const remainingBalance = invoiceAmount - totalAllocatedToOthers;

      // Calculate the amount that would be filled (minimum of remaining balance and row max amount)
      const amountToFill = Math.min(remainingBalance, rowMaxAmount);

      // Validate: Check if there's enough balance
      if (remainingBalance <= 0) {
        showWarningSnackbar(
          `Insufficient balance. Available balance is ₹${MoneyFormatv3(
            invoiceAmount - totalAllocatedToOthers
          )}. Total allocated: ₹${MoneyFormatv3(totalAllocatedToOthers)}`
        );
        return; // Prevent checking the checkbox
      }

      // Validate: Check if row max amount is sufficient
      if (rowMaxAmount <= 0) {
        showWarningSnackbar(`This voucher has no available amount`);
        return; // Prevent checking the checkbox
      }

      // Validate: Check if we can allocate the minimum amount
      if (amountToFill <= 0) {
        showWarningSnackbar(
          `Cannot allocate amount. ` +
            `Remaining balance: ₹${MoneyFormatv3(remainingBalance)}, ` +
            `This voucher's max amount: ₹${MoneyFormatv3(rowMaxAmount)}`
        );
        return; // Prevent checking the checkbox
      }

      // Enough balance available, proceed with checking and filling
      setCheckedRows((prev) => ({
        ...prev,
        [invoiceId]: true,
      }));

      setApplyToAmounts((prev) => ({
        ...prev,
        [invoiceId]: amountToFill,
      }));
    } else {
      // Unchecking - set the amount to 0 and uncheck the checkbox
      setCheckedRows((prev) => {
        const updated = { ...prev };
        updated[invoiceId] = false;
        return updated;
      });

      setApplyToAmounts((prev) => ({
        ...prev,
        [invoiceId]: 0,
      }));
    }
  };

  const handleApply = async () => {
    const selectedData = setOffData
      ?.filter((item) => voucherId !== item?.invoiceid)
      .map((item) => {
        const invoiceId = item?.invoiceid?.toString() || '';
        const amount = applyToAmounts[invoiceId] || 0;
        const hadLinkedToTarget = (initialLinkedToTarget[invoiceId] ?? 0) > 0;

        return {
          invoiceId: item?.invoiceid,
          invoiceNumber: item?.invoicenumber,
          transactionType: item?.transactiontype,
          amount: amount,
          hadLinkedToTarget: hadLinkedToTarget,
        };
      })
      .filter((item) => {
        // Include if: amount > 0 OR had linked_to_target initially (even if changed to 0)
        return item.amount > 0 || item.hadLinkedToTarget;
      });

    if (!selectedData || selectedData.length === 0) {
      showWarningSnackbar('Please enter amounts to apply Set-Off');
      return;
    }

    try {
      // Create set off for each selected item
      const setOffOfitems = selectedData.map((item) => {
        return {
          setoffamount: Number(item.amount),
          sourceinvoiceid: Number(item.invoiceId),
          sourcetransactiontype: String(item.transactionType),
        };
      });
      const res = await createSetOffDetails({
        targetinvoiceid: voucherId,
        targettransactiontype: voucherType,
        setoffitems: setOffOfitems,
      }).unwrap();

      console.log('Set off response:', res);

      // Check the response structure - handle both possible formats
      const responses = res?.createSetOffBatch || [];

      // Check if any response has success: false
      const failedResponses = responses.filter((item) => item?.success === false);

      if (failedResponses.length > 0) {
        // Show error messages for failed responses
        const errorMessages = failedResponses.map((item) => item?.message || 'Unknown error').join(', ');
        showWarningSnackbar(errorMessages);
        // Don't close modal on error
        return;
      }

      // All successful, invalidate query (will refetch automatically when modal opens next time)
      console.log('Set off applied successfully');
      showSuccessSnackbar(`Set-Off applied successfully for ${selectedData.length} voucher(s)`);
      setApplyToAmounts({});
      setCheckedRows({});
      setInitialLinkedToTarget({});

      // Call onSetOffSuccess callback to refetch parent data
      if (onSetOffSuccess) {
        onSetOffSuccess();
      }

      onClose();
    } catch (error: any) {
      console.error('Error applying set off:', error);
      const errorMessage = error?.data?.message || error?.message || 'Failed to apply Set-Off. Please try again.';
      showWarningSnackbar(errorMessage);
      // Don't close modal on error
    }
  };

  const handleClose = () => {
    // Reset form
    setApplyToAmounts({});
    setCheckedRows({});
    setInitialLinkedToTarget({});
    onClose();
  };

  // Calculate total amount entered
  const totalAmount = Object.values(applyToAmounts).reduce((sum, val) => sum + val, 0);

  // Calculate balance: invoiceAmount - totalAmount
  const effectiveBalance = useMemo(() => {
    return invoiceAmount - totalAmount;
  }, [invoiceAmount, totalAmount]);

  const filteredSetOffData = useMemo(() => {
    if (!setOffData || setOffData.length === 0) return [];
    const filtered = setOffData.filter((item) => voucherId !== item?.invoiceid) || [];
    dataVersionRef.current += 1;
    return filtered;
  }, [setOffData, voucherId]);

  // Don't render if required props are missing
  if (!voucherType || !voucherId || !voucherNumber) {
    return null;
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={{
        '& > .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        },
        '& > .MuiBox-root': {
          width: '100% !important',
          maxWidth: '940px !important',
          height: 'calc(100dvh - 76px) !important',
          maxHeight: 'calc(100dvh - 76px) !important',
          top: '0 !important',
          left: '50% !important',
          transform: 'translateX(-50%) !important',
          borderRadius: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
    >
      <Modal.Header>
        <Box
          sx={{
            backgroundColor: 'primary.main',
            padding: '3px 15px',
            display: 'flex',
            flexDirection: 'row',
            position: 'sticky',
            zIndex: 1,
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography
              sx={{
                color: '#fff',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              Set-Off Against Vr. / Invoice No: {voucherNumber}
            </Typography>
          </Box>
          <IconButton aria-label="close" onClick={handleClose} sx={{ color: '#fff' }}>
            <CloseIcon sx={{ color: '#fff' }} />
          </IconButton>
        </Box>
      </Modal.Header>

      <Modal.Body>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            backgroundColor: 'background.paper',
            overflow: 'hidden',
            height: '100%',
          }}
        >
          {loading && (
            <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <TableContainer component={Paper} sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#E6CED7' }}>
                      <TableCell
                        sx={{ backgroundColor: 'background.paper', fontWeight: 'bold', width: 50, padding: 0, paddingX: '5px' }}
                      ></TableCell>
                      <TableCell sx={{ backgroundColor: 'background.paper', fontWeight: 'bold', width: 100, padding: 0, paddingX: '5px' }}>
                        Voucher Date
                      </TableCell>
                      <TableCell sx={{ backgroundColor: 'background.paper', fontWeight: 'bold', width: 100, padding: 0, paddingX: '5px' }}>
                        Voucher No
                      </TableCell>
                      <TableCell sx={{ backgroundColor: 'background.paper', fontWeight: 'bold', width: 120, padding: 0, paddingX: '5px' }}>
                        Voucher Type
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ backgroundColor: 'background.paper', fontWeight: 'bold', width: 200, padding: 0, paddingX: '5px' }}
                      >
                        Total Amt.
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ backgroundColor: 'background.paper', fontWeight: 'bold', width: 200, padding: 0, paddingX: '5px' }}
                      >
                        Available To Set-Off
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          backgroundColor: 'background.paper',
                          fontWeight: 'bold',
                          width: 200,
                          padding: 0,
                          paddingLeft: '5px',
                          paddingRight: '5px',
                          // borderLeft: '2px solid red !important',
                          // borderRight: '2px solid red !important',
                          // borderTop: '2px solid red !important',
                        }}
                      >
                        Apply To Voucher
                      </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: 'background.paper',
                          width: 10,
                          padding: 0,
                        }}
                      ></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...Array(5)].map((_, index) => (
                      <TableRow key={index} sx={{ '& > td': { lineHeight: 1.2 } }}>
                        <CustomTableCell sx={{ width: 50, padding: 0, paddingX: '5px' }}>
                          <Skeleton variant="rectangular" width={20} height={20} />
                        </CustomTableCell>
                        <CustomTableCell sx={{ padding: 0, paddingX: '5px' }}>
                          <Skeleton variant="text" width="80%" height={20} />
                        </CustomTableCell>
                        <CustomTableCell sx={{ padding: 0, paddingX: '5px' }}>
                          <Skeleton variant="text" width="60%" height={20} />
                        </CustomTableCell>
                        <CustomTableCell sx={{ padding: 0, paddingX: '5px' }}>
                          <Skeleton variant="text" width="70%" height={20} />
                        </CustomTableCell>
                        <CustomTableCell align="right" sx={{ padding: 0, paddingX: '5px' }}>
                          <Skeleton variant="text" width="50%" height={20} />
                        </CustomTableCell>
                        <CustomTableCell align="right" sx={{ padding: 0, paddingX: '5px' }}>
                          <Skeleton variant="text" width="50%" height={20} />
                        </CustomTableCell>
                        <CustomTableCell
                          sx={{
                            minWidth: 100,
                            padding: 0,
                            paddingLeft: '5px',
                            paddingRight: '5px',
                            overflow: 'hidden',
                            // borderLeft: '2px solid red !important',
                            // borderRight: '2px solid red !important',
                          }}
                        >
                          <Skeleton variant="rectangular" width="100%" height={32} sx={{ borderRadius: 1 }} />
                        </CustomTableCell>
                        <CustomTableCell
                          sx={{
                            width: 10,
                            padding: 0,
                          }}
                        ></CustomTableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {error ? (
            <Box sx={{ textAlign: 'center', py: 4, p: 2 }}>
              <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                Error loading Set-Off details. Please try again.
              </Typography>
              <Button variant="outlined" onClick={handleRetry} disabled={loading}>
                Retry
              </Button>
            </Box>
          ) : null}

          {!loading && !error && filteredSetOffData.length > 0 && (
            <>
              <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <Box
                  sx={{
                    backgroundColor: '#E6CED7',
                    paddingLeft: '60px',
                    paddingRight: '60px',
                    marginBottom: '5px',
                    width: 'auto',
                    display: 'flex',
                    justifyContent: 'space-between',
                    paddingTop: '4px',
                    paddingBottom: '4px',
                  }}
                >
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography sx={{ paddingBottom: '1px', fontSize: '16px', fontWeight: 'bold', color: 'black' }}>
                      Vr. / Invoice No
                    </Typography>
                    <Typography sx={{ paddingTop: '1px', fontSize: '16px', fontWeight: 'bold', color: 'black' }}>
                      {voucherNumber}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography sx={{ paddingBottom: '1px', fontSize: '16px', fontWeight: 'bold', color: 'black' }}>
                      Vr. / Invoice Amount
                    </Typography>
                    <Typography sx={{ paddingTop: '1px', fontSize: '16px', fontWeight: 'bold', color: 'black' }}>
                      {MoneyFormatv3(invoiceAmount)}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography sx={{ paddingBottom: '1px', fontSize: '16px', fontWeight: 'bold', color: 'black' }}>
                      Applied To Voucher
                    </Typography>
                    <Typography sx={{ paddingTop: '1px', fontSize: '16px', fontWeight: 'bold', color: 'red' }}>
                      {MoneyFormatv3(totalAmount)}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography sx={{ paddingBottom: '1px', fontSize: '16px', fontWeight: 'bold', color: 'black' }}>
                      After Set-Off Now
                    </Typography>
                    <Typography sx={{ paddingTop: '1px', fontSize: '16px', fontWeight: 'bold', color: 'red' }}>
                      {MoneyFormatv3(invoiceAmount - EnteredSetOff - (totalAmount - EnteredSetOff))}
                    </Typography>
                  </Box>
                </Box>

                <TableContainer component={Paper} sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
                  <Table
                    key={`setoff-table-${voucherId}-${dataVersionRef.current}`}
                    stickyHeader
                    size="small"
                    sx={
                      {
                        // '& .MuiTableRow-root:hover .MuiTableCell-root': {
                        //   '&[style*="border-left: 2px solid red"], &[style*="borderLeft"]': {
                        //     borderLeft: '2px solid red !important',
                        //   },
                        //   '&[style*="border-right: 2px solid red"], &[style*="borderRight"]': {
                        //     borderRight: '2px solid red !important',
                        //   },
                        //   '&[style*="border-top: 2px solid red"], &[style*="borderTop"]': {
                        //     borderTop: '2px solid red !important',
                        //   },
                        //   '&[style*="border-bottom: 2px solid red"], &[style*="borderBottom"]': {
                        //     borderBottom: '2px solid red !important',
                        //   },
                        // },
                      }
                    }
                  >
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#E6CED7' }}>
                        <TableCell
                          sx={{ backgroundColor: 'background.paper', fontWeight: 'bold', width: 50, padding: 0, paddingX: '5px' }}
                        ></TableCell>
                        <TableCell
                          sx={{ backgroundColor: 'background.paper', fontWeight: 'bold', width: 110, padding: 0, paddingX: '5px' }}
                        >
                          Voucher Date
                        </TableCell>
                        <TableCell
                          sx={{ backgroundColor: 'background.paper', fontWeight: 'bold', width: 100, padding: 0, paddingX: '5px' }}
                        >
                          Voucher No
                        </TableCell>
                        <TableCell
                          sx={{ backgroundColor: 'background.paper', fontWeight: 'bold', width: 120, padding: 0, paddingX: '5px' }}
                        >
                          Voucher Type
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ backgroundColor: 'background.paper', fontWeight: 'bold', width: 200, padding: 0, paddingX: '5px' }}
                        >
                          Total Amt.
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ backgroundColor: 'background.paper', fontWeight: 'bold', width: 200, padding: 0, paddingX: '5px' }}
                        >
                          Available To Set-Off
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            backgroundColor: 'background.paper',
                            fontWeight: 'bold',
                            width: 200,
                            padding: 0,
                            paddingLeft: '5px',
                            paddingRight: '0px',
                            // borderLeft: '2px solid red !important',
                            // borderRight: '2px solid red !important',
                            // borderTop: '2px solid red !important',
                            // '&:hover': {
                            //   borderLeft: '2px solid red !important',
                            //   borderRight: '2px solid red !important',
                            //   borderTop: '2px solid red !important',
                            // },
                            // '&:active': {
                            //   borderLeft: '2px solid red !important',
                            //   borderRight: '2px solid red !important',
                            //   borderTop: '2px solid red !important',
                            // },
                            // '&:focus': {
                            //   borderLeft: '2px solid red !important',
                            //   borderRight: '2px solid red !important',
                            //   borderTop: '2px solid red !important',
                            // },
                            // '&:focus-visible': {
                            //   borderLeft: '2px solid red !important',
                            //   borderRight: '2px solid red !important',
                            //   borderTop: '2px solid red !important',
                            // },
                            // '&:focus-within': {
                            //   borderLeft: '2px solid red !important',
                            //   borderRight: '2px solid red !important',
                            //   borderTop: '2px solid red !important',
                            // },
                          }}
                        >
                          Apply To Voucher
                        </TableCell>
                        <TableCell
                          sx={{
                            backgroundColor: 'background.paper',
                            width: 82,
                            padding: 0,
                          }}
                        ></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredSetOffData.map((item, index) => {
                        const invoiceId = item?.invoiceid?.toString() || '';
                        const rowAvailableAmount = item?.available_amount || 0;
                        const linkedToTarget = item?.linked_to_target || 0;
                        const isChecked = checkedRows[invoiceId] || false;
                        // Use linked_to_target as initial value if it exists, otherwise use current amount or 0
                        const currentValue = applyToAmounts[invoiceId] ?? (linkedToTarget > 0 ? linkedToTarget : null);

                        // Calculate live balance amount
                        const liveBalanceAmount = (() => {
                          if (!linkedToTarget) {
                            // No previous amount linked
                            return rowAvailableAmount - (applyToAmounts[invoiceId] || 0);
                          } else {
                            // Previous amount exists
                            const newAmount = applyToAmounts[invoiceId] !== undefined ? applyToAmounts[invoiceId] : linkedToTarget;
                            const difference = newAmount - linkedToTarget;
                            return rowAvailableAmount - difference;
                          }
                        })();

                        return (
                          <TableRow key={invoiceId || index} sx={{ '& > td': { lineHeight: 1.2 } }}>
                            <CustomTableCell sx={{ width: 50, padding: 0, paddingX: '5px' }}>
                              <Checkbox
                                checked={isChecked}
                                onChange={(e) => handleCheckboxChange(invoiceId, e.target.checked, rowAvailableAmount, linkedToTarget)}
                                size="small"
                              />
                            </CustomTableCell>
                            <CustomTableCell sx={{ padding: 0, paddingX: '5px' }}>
                              {item?.invoicedate ? formatUnixTimestamp(item.invoicedate) : '-'}
                            </CustomTableCell>
                            <CustomTableCell sx={{ padding: 0, paddingX: '5px' }}>{item?.invoicenumber || '-'}</CustomTableCell>
                            <CustomTableCell sx={{ padding: 0, paddingX: '5px' }}>{item?.transactiontypedesc || '-'}</CustomTableCell>
                            <CustomTableCell align="right" sx={{ padding: 0, paddingX: '5px' }}>
                              ₹{MoneyFormatv3(item?.invoiceamount_converted ?? 0)}
                            </CustomTableCell>
                            <CustomTableCell align="right" sx={{ padding: 0, paddingX: '5px' }}>
                              ₹{MoneyFormatv3(liveBalanceAmount)}
                            </CustomTableCell>
                            <CustomTableCell
                              className="red-border-cell"
                              sx={{
                                minWidth: 100,
                                padding: 0,
                                overflow: 'hidden',
                                '&.MuiTableCell-root.red-border-cell, &.MuiTableCell-root.red-border-cell:hover, &.MuiTableCell-root.red-border-cell:active, &.MuiTableCell-root.red-border-cell:focus, &.MuiTableCell-root.red-border-cell:focus-visible, &.MuiTableCell-root.red-border-cell:focus-within':
                                  {
                                    border: 'none !important',
                                    // borderRight: '2px solid red !important',
                                  },
                              }}
                            >
                              <MoneyTextField
                                value={currentValue}
                                onChange={(e) => handleAmountChange(invoiceId, e)}
                                min={0}
                                max={rowAvailableAmount + (initialLinkedToTarget[invoiceId] || 0)}
                                sx={{
                                  width: '100%',
                                }}
                              />
                            </CustomTableCell>
                            <CustomTableCell
                              sx={{
                                width: 10,
                                padding: 0,
                              }}
                            ></CustomTableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          align="right"
                          sx={{ fontWeight: 'bold', paddingRight: '15px', fontSize: '16px', color: 'black' }}
                        >
                          Total
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', padding: '5px', fontSize: '16px', color: 'black' }}>
                          {MoneyFormatv3(totalInvoiceAmount)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', padding: '5px', fontSize: '16px', color: 'black' }}>
                          {MoneyFormatv3(balancetoSetoff - (totalAmount - EnteredSetOff))}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            color: 'red',
                            fontWeight: 'bold',
                            fontSize: '16px',
                            paddingRight: '0px',
                            // borderLeft: '2px solid red !important',
                            // borderRight: '2px solid red !important',
                            // borderBottom: '2px solid red !important',
                            // '&:hover': {
                            //   borderLeft: '2px solid red !important',
                            //   borderRight: '2px solid red !important',
                            //   borderBottom: '2px solid red !important',
                            // },
                            // '&:active': {
                            //   borderLeft: '2px solid red !important',
                            //   borderRight: '2px solid red !important',
                            //   borderBottom: '2px solid red !important',
                            // },
                            // '&:focus': {
                            //   borderLeft: '2px solid red !important',
                            //   borderRight: '2px solid red !important',
                            //   borderBottom: '2px solid red !important',
                            // },
                            // '&:focus-visible': {
                            //   borderLeft: '2px solid red !important',
                            //   borderRight: '2px solid red !important',
                            //   borderBottom: '2px solid red !important',
                            // },
                            // '&:focus-within': {
                            //   borderLeft: '2px solid red !important',
                            //   borderRight: '2px solid red !important',
                            //   borderBottom: '2px solid red !important',
                            // },
                          }}
                        >
                          {MoneyFormatv3(totalAmount)}
                        </TableCell>
                        <TableCell
                          sx={{
                            width: 10,
                            padding: 0,
                          }}
                        ></TableCell>
                      </TableRow>
                    </TableFooter>
                  </Table>
                </TableContainer>
              </Box>

              {/* <Box
                sx={{
                  p: 2,
                  backgroundColor: 'grey.50',
                  borderRadius: 1,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  position: 'sticky',
                  bottom: 0,
                  zIndex: 1,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Total Amount Entered: ₹{MoneyFormatv3(totalAmount)} / ₹{MoneyFormatv3(invoiceAmount)}
                </Typography>
              </Box> */}
            </>
          )}

          {!loading && !error && filteredSetOffData.length === 0 && setOffData && setOffData.length > 0 && (
            <Box sx={{ textAlign: 'center', py: 4, p: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                No vouchers to Set-Off
              </Typography>
              <Button variant="outlined" onClick={handleRetry}>
                Try Again
              </Button>
            </Box>
          )}

          {!loading && !error && (!setOffData || setOffData.length === 0) && (
            <Box sx={{ textAlign: 'center', py: 4, p: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                No vouchers available
              </Typography>
              <Button variant="outlined" onClick={handleRetry}>
                Try Again
              </Button>
            </Box>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            p: 2,
            width: '100%',
            flexShrink: 0,
            backgroundColor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            boxSizing: 'border-box',
            overflow: 'visible',
          }}
        >
          <Button
            onClick={handleApply}
            variant="contained"
            disabled={loading || !!error || filteredSetOffData.length === 0 || isCreating}
            sx={{
              minWidth: 100,
              backgroundColor: '#ee5b2b',
              '&:hover': {
                backgroundColor: '#ee5b2b',
              },
              '&:active': {
                backgroundColor: '#ee5b2b',
              },
              '&.Mui-focusVisible': {
                backgroundColor: '#ee5b2b',
              },
            }}
          >
            {isCreating ? 'Applying...' : 'Apply'}
          </Button>
        </Box>
      </Modal.Footer>
    </Modal>
  );
};
